import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Calculator } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

type Frequency = "monthly" | "fortnightly" | "weekly";

interface RepaymentCalculatorProps {
  onValuesChange?: (maxLoan: number, term: number) => void;
}

const fmt = (n: number) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n);

export function RepaymentCalculator({ onValuesChange }: RepaymentCalculatorProps) {
  const [targetRepayment, setTargetRepayment] = useState(600);
  const [term, setTerm] = useState(5);
  const [rate, setRate] = useState(7.0);
  const [frequency, setFrequency] = useState<Frequency>("monthly");

  const results = useMemo(() => {
    // Convert target repayment to monthly equivalent
    const Rm =
      frequency === "fortnightly" ? targetRepayment * 26 / 12
        : frequency === "weekly" ? targetRepayment * 52 / 12
          : targetRepayment;

    const im = rate / 100 / 12;
    const Nm = term * 12;

    let P: number;
    if (im === 0) {
      P = Rm * Nm;
    } else {
      P = Rm * (Math.pow(1 + im, Nm) - 1) / (im * Math.pow(1 + im, Nm));
    }

    // Compute equivalent repayments in all frequencies
    const monthly = im === 0 ? P / Nm : (P * im * Math.pow(1 + im, Nm)) / (Math.pow(1 + im, Nm) - 1);
    const fortnightly = monthly * 12 / 26;
    const weekly = monthly * 12 / 52;

    const totalMonths = Nm;
    const totalRepaid = monthly * totalMonths;
    const totalInterest = totalRepaid - P;

    return { maxLoan: Math.round(P), monthly, fortnightly, weekly, totalRepaid, totalInterest };
  }, [targetRepayment, term, rate, frequency]);

  useEffect(() => {
    onValuesChange?.(results.maxLoan, term);
  }, [results.maxLoan, term, onValuesChange]);

  const interestPercent = results.totalRepaid > 0 ? (results.totalInterest / results.totalRepaid) * 100 : 0;
  const freqLabel = frequency === "fortnightly" ? "Fortnightly" : frequency === "weekly" ? "Weekly" : "Monthly";

  return (
    <section>
      <div className="container max-w-4xl">
        <h2 className="mb-10 text-center font-display text-3xl font-bold md:text-4xl">
          Car Loan Repayment Calculator
        </h2>
        <div className="grid gap-8 md:grid-cols-2">
          {/* Inputs */}
          <div className="space-y-8">
            <div>
              <Label className="mb-3 flex items-center justify-between">
                <span>Target {freqLabel} Repayment</span>
                <span className="font-display text-lg font-bold text-primary">{fmt(targetRepayment)}</span>
              </Label>
              <Slider
                value={[targetRepayment]}
                onValueChange={([v]) => setTargetRepayment(v)}
                min={50}
                max={3000}
                step={10}
              />
              <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                <span>$50</span><span>$3,000</span>
              </div>
            </div>
            <div>
              <Label className="mb-3 flex items-center justify-between">
                <span>Loan Term</span>
                <span className="font-display text-lg font-bold text-primary">{term} years</span>
              </Label>
              <Slider
                value={[term]}
                onValueChange={([v]) => setTerm(v)}
                min={1}
                max={7}
                step={1}
              />
              <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                <span>1 year</span><span>7 years</span>
              </div>
            </div>
            <div>
              <Label className="mb-3 flex items-center justify-between">
                <span>Interest Rate</span>
                <span className="font-display text-lg font-bold text-primary">{rate.toFixed(1)}% p.a.</span>
              </Label>
              <Slider
                value={[rate]}
                onValueChange={([v]) => setRate(v)}
                min={3}
                max={30}
                step={0.1}
              />
              <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                <span>3%</span><span>30%</span>
              </div>
            </div>
            <div>
              <Label className="mb-3 block">Repayment Frequency</Label>
              <ToggleGroup
                type="single"
                value={frequency}
                onValueChange={(v) => { if (v) setFrequency(v as Frequency); }}
                className="justify-start"
              >
                <ToggleGroupItem value="monthly" className="text-xs px-3">Monthly</ToggleGroupItem>
                <ToggleGroupItem value="fortnightly" className="text-xs px-3">Fortnightly</ToggleGroupItem>
                <ToggleGroupItem value="weekly" className="text-xs px-3">Weekly</ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>

          {/* Results */}
          <Card className="border-2 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display">
                <Calculator className="h-5 w-5 text-primary" />
                Your Estimate
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Maximum Loan Amount</p>
                <p className="font-display text-4xl font-bold text-primary">{fmt(results.maxLoan)}</p>
              </div>

              {/* Equivalent repayments */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-muted p-2">
                  <p className="text-xs text-muted-foreground">Weekly</p>
                  <p className="font-display text-sm font-bold">{fmt(results.weekly)}</p>
                </div>
                <div className="rounded-lg bg-muted p-2">
                  <p className="text-xs text-muted-foreground">Fortnightly</p>
                  <p className="font-display text-sm font-bold">{fmt(results.fortnightly)}</p>
                </div>
                <div className="rounded-lg bg-muted p-2">
                  <p className="text-xs text-muted-foreground">Monthly</p>
                  <p className="font-display text-sm font-bold">{fmt(results.monthly)}</p>
                </div>
              </div>

              {/* Visual bar */}
              <div>
                <div className="mb-2 flex h-4 w-full overflow-hidden rounded-full">
                  <div className="bg-primary transition-all" style={{ width: `${100 - interestPercent}%` }} />
                  <div className="bg-accent transition-all" style={{ width: `${interestPercent}%` }} />
                </div>
                <div className="flex justify-between text-xs">
                  <span className="flex items-center gap-1">
                    <span className="inline-block h-2.5 w-2.5 rounded-full bg-primary" />
                    Principal: {fmt(results.maxLoan)}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block h-2.5 w-2.5 rounded-full bg-accent" />
                    Interest: {fmt(results.totalInterest)}
                  </span>
                </div>
              </div>

              <div className="rounded-lg bg-muted p-3 text-center">
                <p className="text-sm text-muted-foreground">Total Repayment</p>
                <p className="font-display text-xl font-bold">{fmt(results.totalRepaid)}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
